'use client';

import LoginForm from '@/components/auth/LoginForm';
import { useSupabaseAuth } from '@/lib/auth/simplified/SupabaseAuthContext';
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';

// Import AuthDebug only on client side with no SSR to avoid hydration issues
const AuthDebug = dynamic(() => import('./AuthDebug'), { ssr: false });

// Client-only wrapper component to ensure AuthDebug is only rendered on client
function ClientOnlyAuthDebug() {
  const [isMounted, setIsMounted] = useState(false);
  
  useEffect(() => {
    setIsMounted(true);
  }, []);
  
  if (!isMounted || process.env.NODE_ENV === 'production') return null;
  return <AuthDebug />;
}

// Map of error codes to user-friendly messages
const ERROR_MESSAGES: Record<string, string> = {
  'auth_error': 'Authentication failed. Please try again.',
  'profile_creation_failed': 'Failed to create user profile. Please contact support.',
  'missing_code': 'Invalid login link. Please request a new one.',
  'unexpected': 'An unexpected error occurred. Please try again.',
};

interface LoginContentProps {
  error?: string;
  message?: string;
  redirectForAuth?: boolean;
  isSignUp?: boolean;
}

// A component that safely uses searchParams inside a Suspense boundary
function LoginContent({ error, message, redirectForAuth, isSignUp }: LoginContentProps) {
  const { isAuthenticated, isLoading, status: authStatus } = useSupabaseAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const urlSignUp = searchParams.get('signup') === 'true';
  // Use either the prop or the URL parameter
  const shouldShowSignUp = isSignUp || urlSignUp;
  
  // Set mounted state to true on client-side and add URL listener
  useEffect(() => {
    setMounted(true);
    
    // Set up a listener for URL changes
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      const signupParam = params.get('signup');
      console.log('[LoginClient] URL changed, signup param:', signupParam);
    };
    
    // Add event listener for URL changes
    window.addEventListener('popstate', handleUrlChange);
    
    // Clear orphaned auth verifiers that cause crashes
    try {
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        const projectRef = process.env.NEXT_PUBLIC_SUPABASE_URL?.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] || 'lpjvndsvkvqgkdkvacnn';
        const tokenKey = `sb-${projectRef}-auth-token`;
        const verifierKey = `${tokenKey}-code-verifier`;
        
        // If we have inconsistent state, clean it up
        if (!localStorage.getItem(tokenKey) && localStorage.getItem(verifierKey)) {
          console.log('[LoginClient] Cleaning up inconsistent auth state');
          localStorage.removeItem(verifierKey);
        }
      }
    } catch (e) {
      console.error('[LoginClient] Error cleaning up auth state:', e);
    }
    
    // Clean up the event listener
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []);
  
  useEffect(() => {
    if (!mounted) return;
    
    // Check for authentication errors in URL params or from prop
    const errorCode = error || searchParams.get('error');
    if (errorCode && ERROR_MESSAGES[errorCode]) {
      setErrorMessage(ERROR_MESSAGES[errorCode]);
    } else if (error) {
      setErrorMessage(error);
    } else if (searchParams.get('auth_state_reset') === 'true') {
      setErrorMessage('Your authentication state was reset due to a session issue. Please sign in again.');
    }
    
    // Show success message if provided
    if (message) {
      // Handle success message display if needed
    }
    
    // Verify server-side auth status on load (but with debounce)
    const verifySeverSession = async () => {
      // Only verify server auth if client reports authenticated
      if (isAuthenticated && !isLoading && authStatus === 'authenticated') {
        try {
          console.log('[LoginClient] Validating server auth session...');
          const response = await fetch('/api/debug-auth');
          
          console.log('[LoginClient] Server response status:', response.status);
          
          if (response.ok) {
            const data = await response.json();
            console.log('[LoginClient] Server auth response:', JSON.stringify(data, null, 2));
            const hasServerAuth = data?.auth?.hasValidSession === true;
            
            if (hasServerAuth) {
              // Server confirms auth - we should redirect
              console.log('[LoginClient] Server confirms authentication, redirecting');
              const hasProfile = data?.profile?.exists === true;
              
              // Use window.location.replace for a stronger redirect that forces page reload
              // The NextJS router.push may not trigger a full page reload which we need here
              console.log('[LoginClient] Forcing hard redirect to:', hasProfile ? '/dashboard/moments' : '/onboarding');
              
              // Add a small delay to ensure auth state is fully processed
              setTimeout(() => {
                // Add timestamp parameter to force a full page reload and bypass cache
                const timestamp = Date.now();
                window.location.replace(`${hasProfile ? '/dashboard/moments' : '/onboarding'}?ts=${timestamp}`);
              }, 300);
            } else {
              // Server disagrees with client - auth state inconsistency
              console.log('[LoginClient] Server reports not authenticated but client says authenticated');
              
              // Reset client auth state to match server
              if (typeof localStorage !== 'undefined') {
                const projectRef = process.env.NEXT_PUBLIC_SUPABASE_URL?.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] || 'lpjvndsvkvqgkdkvacnn';
                localStorage.removeItem(`sb-${projectRef}-auth-token`);
                localStorage.setItem('auth_state_reset', 'true');
                
                // Force reload to reset auth state
                window.location.replace('/login?auth_state_reset=true');
              }
            }
          }
        } catch (error) {
          console.error('[LoginClient] Error validating auth with server:', error);
        }
      }
    };
    
    // Use a timeout to prevent immediate redirects during page load
    // This gives a chance for auth state to stabilize
    let authCheckTimer: NodeJS.Timeout;
    
    if (isAuthenticated && !isLoading && authStatus === 'authenticated') {
      console.log('[LoginClient] User appears authenticated, scheduling server validation');
      authCheckTimer = setTimeout(verifySeverSession, 800);
    }
    
    // Handle redirect for auth if needed (for branch deployments)
    if (redirectForAuth && typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname.includes('-git-')) {
        // Extract branch name from hostname for Vercel preview URLs
        const branchMatch = hostname.match(/-git-([^-]+)-/);
        if (branchMatch && branchMatch[1]) {
          const branch = branchMatch[1];
          console.log(`[LoginClient] Detected branch deployment: ${branch}`);
          
          // For now, just log detection - we could implement branch-specific logic here
          // such as redirecting to the main domain for authentication if needed
        }
      }
    }
    
    // Clean up timer
    return () => {
      if (authCheckTimer) clearTimeout(authCheckTimer);
    };
  }, [isAuthenticated, isLoading, authStatus, router, searchParams, mounted, error, message, redirectForAuth]);
  
  return (
    <>
      {mounted && errorMessage && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{errorMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Only render the form after client-side hydration is complete */}
      {mounted ? (
        <LoginForm initialSignUpMode={shouldShowSignUp} />
      ) : (
        <div className="h-[360px] w-full bg-white dark:bg-gray-800 rounded-lg shadow-md"></div>
      )}
    </>
  );
}

interface LoginClientProps {
  redirectForAuth?: boolean;
  error?: string;
  message?: string;
  isSignUp?: boolean;
}

export default function LoginClient({ redirectForAuth, error, message, isSignUp }: LoginClientProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-extrabold text-gray-900">
            Apprendo
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Share your expertise through guided conversations
          </p>
        </div>
        
        <Suspense fallback={<div className="h-[400px] w-full bg-white rounded-lg shadow-md animate-pulse"></div>}>
          <LoginContent 
            redirectForAuth={redirectForAuth}
            error={error}
            message={message}
            isSignUp={isSignUp}
          />
        </Suspense>
        
        {/* Hidden AuthDebug component - only rendered on client side */}
        <ClientOnlyAuthDebug />
      </div>
    </div>
  );
}